<template>
  <div class="relative inline-flex" :class="global ? 'mr-1' : ''">
    <Button
      :severity="global ? 'secondary' : 'contrast'"
      :variant="global ? 'text' : undefined"
      :outlined="!global"
      :rounded="global"
      size="small"
      class="p-0 border-none relative"
      :class="global ? 'w-7 h-7' : ''"
      @click="onToggle"
    >
      <template #icon>
        <CalendarReminder :class="global ? 'w-6 h-6' : ''" />
      </template>
    </Button>

    <span
      v-if="dueUnseenCount > 0"
      class="absolute -top-1 -right-1 min-w-4 h-4 px-0.5 rounded-full flex items-center justify-center pointer-events-none text-[10px] font-bold text-white bg-yellow-400 animate-pulse"
    >
      {{ dueUnseenCount > 9 ? '9+' : dueUnseenCount }}
    </span>
  </div>

  <Popover ref="popoverRef" :append-to="global ? 'body' : undefined">
    <div class="w-80 flex flex-col gap-3">
      <div class="flex items-center justify-between">
        <span class="font-semibold text-sm">{{ global ? 'My Reminders' : 'Reminders' }}</span>
        <Button v-if="!global" icon="pi pi-plus" size="small" text label="Add" @click="openForm(null)" />
      </div>

      <DatePicker
        v-model="selectedDate"
        inline
        date-format="dd/mm/yy"
        @date-select="onDateSelect"
        :pt="{
          day: ({ context }) => ({
            class: isDayWithReminder(context.date) ? 'ring-2 ring-blue-400 ring-offset-1 rounded-full' : '',
          }),
        }"
      />

      <template v-if="selectedDate">
        <Divider class="my-0" />

        <div v-if="dayReminders.length" class="flex flex-col gap-2 max-h-48 overflow-y-auto pr-1">
          <div
            v-for="r in dayReminders"
            :key="r.id"
            class="flex flex-col p-2 rounded-lg border transition-colors cursor-pointer hover:bg-surface-50 dark:hover:bg-surface-800"
            :class="r.isSeen ? 'border-surface-200 dark:border-surface-700' : 'border-blue-400 dark:border-blue-500'"
            @click="openForm(r)"
          >
            <div class="flex items-center justify-between gap-2">
              <div class="flex items-center gap-2 min-w-0">
                <i :class="[typeIcon(r.type), 'shrink-0']" />
                <span class="text-xs font-medium">{{ formatTime(r.startDate) }}</span>
              </div>
              <Tag :value="typeLabel(r.type)" severity="secondary" class="text-xs shrink-0 ml-2" />
            </div>
            <div v-if="global && r.customer" class="mt-1 text-xs text-surface-400 truncate">
              {{ r.customer.name }}
            </div>
            <div v-if="r.note" class="text-xs text-surface-400 truncate">{{ r.note }}</div>
          </div>
        </div>

        <p v-else class="text-xs text-surface-400 text-center py-1">No reminders for this day</p>
      </template>
    </div>
  </Popover>

  <Dialog
    v-model:visible="formVisible"
    modal
    :header="editingReminder ? 'Edit Reminder' : 'New Reminder'"
    style="width: 400px"
    :draggable="false"
  >
    <UserReminderForm
      v-if="editingReminder || !global"
      :key="formVisible ? (editingReminder?.id ?? 'new') + String(selectedDate) : 'hidden'"
      :customer-id="editingReminder ? editingReminder.customerId : customerId"
      :reminder="editingReminder"
      :preselected-date="!editingReminder ? selectedDate : null"
      @saved="onSaved"
      @deleted="onDeleted"
    />
  </Dialog>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { Button, DatePicker, Dialog, Divider, Popover, Tag } from 'primevue'
import CalendarReminder from '@/components/icons/CalendarReminder.vue'
import UserReminderForm from './UserReminderForm.vue'
import apiClient from '@/utils/apiClient'
import { useToast } from 'primevue/usetoast'

const props = defineProps({
  customerId: { type: Number, default: null },
  global: { type: Boolean, default: false },
  incomingReminders: { type: Array, default: () => [] },
})

const toast = useToast()
const popoverRef = ref(null)
const formVisible = ref(false)
const editingReminder = ref(null)
const reminders = ref([])
const selectedDate = ref(null)

const dueUnseenCount = computed(
  () => reminders.value.filter((r) => !r.isSeen && new Date(r.startDate) <= new Date()).length
)

const onToggle = (event) => {
  popoverRef.value.toggle(event)
}

const fetchReminders = async () => {
  try {
    const url = props.customerId ? `/user-reminders/customer/${props.customerId}` : '/user-reminders'
    const res = await apiClient.get(url)
    reminders.value = res.data.data
  } catch {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to load reminders.', life: 3000 })
  }
}

const isSameDay = (date, reminderDateStr) => {
  const d = new Date(reminderDateStr)
  return d.getFullYear() === date.getFullYear() && d.getMonth() === date.getMonth() && d.getDate() === date.getDate()
}

const isDayWithReminder = (calendarDate) => {
  if (!calendarDate) return false
  const date = new Date(calendarDate.year, calendarDate.month, calendarDate.day)
  return reminders.value.some((r) => isSameDay(date, r.startDate))
}

const dayReminders = computed(() => {
  if (!selectedDate.value) return []
  return reminders.value.filter((r) => isSameDay(selectedDate.value, r.startDate))
})

const onDateSelect = (date) => {
  selectedDate.value = date
}

const openForm = async (reminder) => {
  editingReminder.value = reminder

  if (reminder && !reminder.isSeen && new Date(reminder.startDate) <= new Date()) {
    try {
      await apiClient.put(`/user-reminders/${reminder.id}/acknowledge`)
      const index = reminders.value.findIndex((r) => r.id === reminder.id)
      if (index !== -1) {
        reminders.value[index] = { ...reminders.value[index], isSeen: true }
      }
    } catch {
      // non-critical
    }
  }

  formVisible.value = true
}

const onSaved = (reminder) => {
  const index = reminders.value.findIndex((r) => r.id === reminder.id)
  if (index !== -1) {
    reminders.value[index] = reminder
  } else {
    reminders.value.push(reminder)
  }
  formVisible.value = false
}

const onDeleted = (id) => {
  reminders.value = reminders.value.filter((r) => r.id !== id)
  formVisible.value = false
}

const typeIcon = (type) =>
  ({
    phone_call: 'pi pi-phone text-green-500',
    email: 'pi pi-envelope text-red-400',
    note: 'pi pi-pen-to-square text-yellow-400',
  })[type] ?? 'pi pi-bell'

const typeLabel = (type) => ({ phone_call: 'Phone', email: 'Email', note: 'Note' })[type] ?? type

const formatTime = (dateStr) => {
  const [, timePart] = dateStr.split(' ')
  return timePart?.slice(0, 5) ?? ''
}

watch(
  () => props.incomingReminders,
  (incoming) => {
    incoming.forEach((reminder) => {
      const index = reminders.value.findIndex((r) => r.id === reminder.id)
      if (index !== -1) {
        reminders.value[index] = { ...reminders.value[index], ...reminder }
      } else {
        reminders.value.push(reminder)
      }
    })
  }
)

onMounted(() => {
  fetchReminders()
})
</script>
